import { List, Map } from 'immutable'
import React from 'react'
import {
  fromJSX,
  loadingAsyncState,
  none,
  notFoundRouteCase,
  route,
  some,
  unloadedAsyncState,
  Updater
} from 'widgets-for-react'
import { PageTemplateComponent_AUX } from '../legacy/public_site/components/cms/page/widget.gen'
import { getAllUpdates, getOverview, search, getCategoryCounts } from '../legacy/public_site/components/drupal/api/api.mod'
import {
  Language,
  ParsedNodeUpdate,
  ParsedTaxonomyTermRelatedContent
} from '../legacy/public_site/components/drupal/types_and_parser/types.ext.gen'
import { getPage } from '../legacy/public_site/components/public_site/application.mod'
import { search_overview } from '../legacy/public_site/components/public_site/custom_pages/search_overview/search_overview.widget'
import {
  getSustainabilityTerms,
  getTaxonomies,
  update_overview,
  UpdateOverviewKind
} from '../legacy/public_site/components/public_site/custom_pages/update_overview'
import { notfound_page } from '../legacy/public_site/components/public_site/templates/error_page.mod'
import { defaultLanguage } from '../legacy/public_site/components/public_site/utils/history.mod'
import { Debounced } from '../legacy/public_site/custom_components/debounce/debounce.state'
import { Styleguide } from '../legacy/public_site/styleguide/styleguide'
import { Pages, PublicSiteState, RootState } from './state'
import { searchOverviewFilterOptions } from '../legacy/public_site/components/public_site/custom_pages/search_overview/search_overview.state'

const parsePageNumber = (page: string | undefined): number | undefined => {
  const pageNumber = page ? parseInt(page) : 0
  return isNaN(pageNumber) ? undefined : pageNumber
}

const updatesOverviewStateUpdater =
  (kind: UpdateOverviewKind) => (params: { page: string; lang: Language; taxonomy: string }) => {
    const pageNumber = parsePageNumber(params.page)
    if (pageNumber === undefined) return drupalPageStateUpdater(`${params.lang}/${kind}/${params.page}`)
    const lang = params.lang || defaultLanguage
    const newPage: Pages = { kind: 'update_overview', page: pageNumber, lang }
    return (state: RootState): RootState => ({
      ...state,
      page: newPage,
      current_widget: some((s2) =>
        fromJSX((setState) =>
          update_overview({
            routes: newPage,
            state: s2.update_overview,
            set_public_state: setState,
            public_state: s2
          })
            .map<Updater<PublicSiteState>>(
              (a) =>
                (s3): PublicSiteState => ({
                  ...s3,
                  update_overview: a(s3.update_overview),
                  cookiestate: a(s3.update_overview).state.cookiestate
                })
            )
            .run(setState)
        )
      ),
      state: {
        ...state.state,
        update_overview: {
          overview_page: pageNumber,
          get_updates: loadingAsyncState(() => getAllUpdates(lang)),
          get_searches:
            state.state.update_overview.current_query != ''
              ? search(state.state.update_overview.current_query, '', 0, 10000, lang)
              : unloadedAsyncState(),
          get_tags: loadingAsyncState(() => getOverview<ParsedTaxonomyTermRelatedContent>('tags', lang)),
          sustainability_tags:
            kind == 'sustainability-updates' ? loadingAsyncState(() => getSustainabilityTerms()) : unloadedAsyncState(),
          current_tags: List(getTaxonomies(kind)),
          updates_jsx: none(),
          current_query: '',
          get_suggestions: unloadedAsyncState(),
          kind,
          state: {
            cookiestate: state.state.cookiestate,
            page: { ...state.state.page, page: getPage(lang ? `${lang}/${kind}` : 'en/updates') }
          }
        }
      }
    })
  }

const drupalPageStateUpdater =
  (slug: string | undefined) =>
  (state: RootState): RootState => ({
    ...state,
    page: { kind: 'page', slug },
    current_widget: some((s2) =>
      fromJSX((setState) =>
        PageTemplateComponent_AUX(
          window.location.href,
          s2.page.pageDisplay(s2, setState),
          s2.page.display,
          s2
        )({
          ...s2.page,
          page:
            s2.page.page.kind == 'loading' || s2.page.page.kind == 'unloaded' ? s2.page.get_page(slug) : s2.page.page
        })
          .map(
            (a) =>
              (s3: PublicSiteState): PublicSiteState => ({ ...s3, page: { ...s3.page, ...a(s3.page) } })
          )
          .run(setState)
      )
    ),
    state: { ...state.state, page: { ...state.state.page, blocks: Map(), page: unloadedAsyncState() } }
  })

export const pages = [
  route<{ _?: string }, Updater<RootState>>(
    '/styleguide*',
    (params) =>
      (state): RootState => ({
        ...state,
        page: { ...state.page, kind: 'styleguide' },
        current_widget: some(() => fromJSX(() => <Styleguide currentPath={params._} />))
      })
  ),
  route<{ page: string; lang: string; query: string; type: string }, Updater<RootState>>(
    '(/:lang)/search(/:query/:page)',
    (params) =>
      (state): RootState => {
        const newPage: Pages = {
          kind: 'search_overview',
          query: params.query ? decodeURIComponent(params.query) : params.query,
          page: params.page && parseInt(params.page),
          lang: params.lang,
          type: params.type
        }
        const page = isNaN(+newPage.page) ? 0 : +newPage.page
        const query = newPage.query ?? ''
        const selected_filter = newPage.type ? newPage.type : ''
        return {
          ...state,
          page: newPage,
          current_widget: some((s2) =>
            fromJSX((setState) =>
              search_overview({
                routes: newPage,
                state: s2[newPage.kind],
                public_state: s2
              })
                .map<Updater<PublicSiteState>>(
                  (a) =>
                    (s3): PublicSiteState => ({ ...s3, search_overview: a(s3[newPage.kind]) })
                )
                .run(setState)
            )
          ),
          state: {
            ...state.state,
            search_overview: {
              pageTypeSelection: {
                ...state.state.search_overview.pageTypeSelection,
                selection: searchOverviewFilterOptions.includes(selected_filter) ? selected_filter : ''
              },
              overview_page: page,
              current_query: Debounced.dirty(query),
              get_suggestions: unloadedAsyncState(),
              get_searches: query ? search(query, selected_filter, page, 10, newPage.lang) : unloadedAsyncState(),
              get_category_counts: query ? getCategoryCounts(newPage.lang, query) : unloadedAsyncState(),
              sortBy: state.state.search_overview.sortBy || 'relevance',
              get_updates: query
                ? loadingAsyncState(() =>
                    getOverview<ParsedTaxonomyTermRelatedContent>('tags', newPage.lang).then((tags) => {
                      let taxonomies =
                        tags.kind == 'result'
                          ? tags.value.items
                              .filter((i) => i.label.toLocaleLowerCase() == query.toLocaleLowerCase())
                              .map((i) => i.id)
                          : []
                      if (taxonomies.length == 0) return { kind: 'failed', status: 200 }
                      return getOverview<ParsedNodeUpdate>('update-overview', newPage.lang, page, List(taxonomies))
                    })
                  )
                : unloadedAsyncState()
            }
          }
        }
      }
  ),
  route<{ page: string; lang: Language; taxonomy: string }, Updater<RootState>>(
    '(/:lang)/updates(/:page)',
    updatesOverviewStateUpdater('updates')
  ),
  route<{ page: string; lang: Language; taxonomy: string }, Updater<RootState>>(
    '(/:lang)/project-updates(/:page)',
    updatesOverviewStateUpdater('project-updates')
  ),
  route<{ page: string; lang: Language; taxonomy: string }, Updater<RootState>>(
    '(/:lang)/sustainability-updates(/:page)',
    updatesOverviewStateUpdater('sustainability-updates')
  ),
  route<{ _?: string }, Updater<RootState>>('*', ({ _ }) => drupalPageStateUpdater(_)),
  notFoundRouteCase<Updater<RootState>>(
    () =>
      (state): RootState => ({
        ...state,
        page: { kind: '404' },
        current_widget: some((_) => notfound_page(state.state)),
        state: {
          ...state.state,
          page: { ...state.state.page, blocks: Map(), page: unloadedAsyncState() }
        }
      })
  )
]
